// src/components/BookGuide.js
import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useAuth } from "../context/AuthContext";
import "../styles/BookGuide.css";
import { useNavigate } from "react-router-dom";

const BookGuide = () => {
    const { loggedIn, loading: authLoading } = useAuth();
    const [guides, setGuides] = useState([]);
    const [filteredGuides, setFilteredGuides] = useState([]);
    const [searchPlace, setSearchPlace] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAvailability, setShowAvailability] = useState({}); // toggle availability list
    const [profileImages, setProfileImages] = useState({}); // store guide email -> image URL

    const API_URL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();

    // Fetch all guides
    useEffect(() => {
        if (!authLoading && !loggedIn) {
            navigate("/login");
            return;
        }
        if (!loggedIn) return;

        const fetchGuides = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`${API_URL}/guides`);
                if (!res.ok) throw new Error("Failed to fetch guides");
                const data = await res.json();
                setGuides(data.guides || []);
                setFilteredGuides(data.guides || []);

                // Fetch profile images for all guides
                const imagesMap = {};
                await Promise.all(
                    (data.guides || []).map(async (g) => {
                        try {
                            const imgRes = await fetch(`${API_URL}/profile-image?userEmail=${encodeURIComponent(g.email)}`);
                            const imgData = await imgRes.json();
                            imagesMap[g.email] = imgData.profileImageUrl || "/default-profile.png";
                        } catch {
                            imagesMap[g.email] = "/default-profile.png";
                        }
                    })
                );
                setProfileImages(imagesMap);
            } catch (err) {
                console.error(err);
                setError("Failed to load guides");
            } finally {
                setLoading(false);
            }
        };

        fetchGuides();
    }, [loggedIn, authLoading, API_URL, navigate]);

    // Search/filter by place
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchPlace(value);

        if (!value.trim()) {
            setFilteredGuides(guides);
            return;
        }

        const filtered = guides.filter((g) =>
            g.places.some((place) =>
                place.toLowerCase().includes(value.toLowerCase())
            )
        );
        setFilteredGuides(filtered);
    };

    const handleBookGuide = (guide) => {
        navigate("/book-guide-confirmation", { state: { guide } });
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    if (authLoading || loading)
        return (
            <>
                <Navbar />
                <div className="loading-destinations">
                    <div className="loading-spinner"></div>
                    <p>Loading guides...</p>
                </div>
                <Footer />
            </>
        );

    if (error)
        return (
            <>
                <Navbar />
                <div className="error-section">
                    <p>{error}</p>
                </div>
                <Footer />
            </>
        );

    return (
        <div className="book-guide-container">
            <Navbar />
            <div className="book-guide-content">
                <h2>Book a Guide</h2>
                <input
                    type="text"
                    placeholder="Search by place..."
                    value={searchPlace}
                    onChange={handleSearch}
                    className="guide-search-bar"
                />

                {!loading && filteredGuides.length === 0 ? (
                    <p className="no-guides">No guides found.</p>
                ) : (
                    <div className="guides-list">
                        {filteredGuides.map((guide) => (
                            <div key={guide._id} className="guide-card">
                                {/* Profile Image */}
                                <img
                                    src={profileImages[guide.email] || "/default-profile.png"}
                                    alt={guide.name}
                                    className="guide-profile-img"
                                    style={{ width: 100, height: 100, borderRadius: "50%" }}
                                />

                                <h3>{guide.name}</h3>
                                <p><strong>Email:</strong> {guide.email}</p>
                                <p><strong>Phone:</strong> {guide.phone}</p>
                                <p>
                                    <strong>Rating:</strong>{" "}
                                    {guide.rating === 0 ? "No ratings yet" : `${guide.rating} ⭐`}
                                </p>
                                <p><strong>Places:</strong> {guide.places.join(", ")}</p>
                                <p>
                                    <strong>Languages:</strong>{" "}
                                    {guide.languages?.length > 0 ? guide.languages.join(", ") : "Not specified"}
                                </p>
                                <p><strong>Description:</strong> {guide.description || "No description provided"}</p>
                                <p><strong>Base Fare:</strong> ₹{guide.baseFare}/{guide.fareType}</p>

                                {guide.reviewLinks?.length > 0 && (
                                    <div className="review-links">
                                        <strong>Reviews:</strong>
                                        <ul>
                                            {guide.reviewLinks.map((link, i) => (
                                                <li key={i}>
                                                    <a href={link} target="_blank" rel="noopener noreferrer">
                                                        {link}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Toggle Availability */}
                                <button
                                    className="btn-availability"
                                    onClick={() =>
                                        setShowAvailability((prev) => ({ ...prev, [guide._id]: !prev[guide._id] }))
                                    }
                                >
                                    {showAvailability[guide._id] ? "Hide Availability" : "Show Availability"}
                                </button>

                                {showAvailability[guide._id] && (
                                    <ul className="availability-list">
                                        {guide.availableDates?.length > 0
                                            ? guide.availableDates.map((d, i) => <li key={i}>{formatDate(d)}</li>)
                                            : <li>No dates available</li>}
                                    </ul>
                                )}

                                <button className="btn-book-guide" onClick={() => handleBookGuide(guide)}>
                                    Book Guide
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default BookGuide;
